"use client";

import * as React from "react";

import { MARKET_COUNTRY_CODES, countryName, compareCountryCodes } from "~/lib/markets/countries";
import { formatMoney } from "~/lib/money";
import { api } from "~/trpc/react";

/** A read-only destination estimate for the currently selected PDP variant. */
export function PdpShippingEstimate({
  variantId,
  quantity,
  defaultCountry = "GB",
  recoveryScope = "",
}: {
  variantId: string | null;
  quantity: number;
  defaultCountry?: string;
  /** Brand scope prevents a destination stored on another storefront from leaking here. */
  recoveryScope?: string;
}) {
  const [countryInput, setCountryInput] = React.useState("");
  const [region, setRegion] = React.useState("");
  const [postcode, setPostcode] = React.useState("");
  const [submittedDestination, setSubmittedDestination] = React.useState<{
    country: string;
    postcode: string;
    region: string;
    variantId: string;
    quantity: number;
  } | null>(null);
  const [validationError, setValidationError] = React.useState<string | null>(null);
  const country = countryInput.trim().toUpperCase();
  const effectiveRegion = country === "GB" ? "" : region.trim();
  const storageKey = `pdp-delivery:${recoveryScope}`;
  React.useEffect(() => {
    if (!recoveryScope) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) ?? "null") as { country?: unknown; postcode?: unknown; region?: unknown } | null;
      if (saved && typeof saved.country === "string" && MARKET_COUNTRY_CODES.includes(saved.country)) {
        setCountryInput(saved.country);
        setPostcode(typeof saved.postcode === "string" ? saved.postcode : "");
        setRegion(saved.country === "GB" ? "" : typeof saved.region === "string" ? saved.region : "");
      }
    } catch { /* A blocked storage surface still permits read-only estimates. */ }
  }, [storageKey, recoveryScope]);
  const previewCountry = country || defaultCountry;
  const preview = api.shipping.estimateProduct.useQuery({ variantId: variantId ?? "unselected", quantity, country: previewCountry }, { enabled: !!variantId && previewCountry.length === 2 });
  const estimateInput = {
    variantId: variantId ?? "unselected",
    quantity,
    country: country.length === 2 ? country : "GB",
    postcode: postcode.trim() || "placeholder",
    ...(effectiveRegion ? { region: effectiveRegion } : {}),
  };
  const estimate = api.shipping.estimateProduct.useQuery(estimateInput, {
    enabled: false,
  });

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!variantId) {
      setValidationError("Choose a product option before estimating delivery.");
      return;
    }
    if (country.length !== 2 || postcode.trim().length === 0) {
      setValidationError("Choose a country and enter a postcode.");
      return;
    }
    setValidationError(null);
    const destination = {
      country,
      region: effectiveRegion,
      postcode: postcode.trim(),
      variantId,
      quantity,
    };
    setSubmittedDestination(destination);
    if (recoveryScope) {
      try { sessionStorage.setItem(storageKey, JSON.stringify({ country, postcode: destination.postcode, region: effectiveRegion })); } catch { /* Estimate remains usable without storage. */ }
    }
    await estimate.refetch();
  };

  const result = submittedDestination?.variantId === variantId &&
    submittedDestination.quantity === quantity &&
    submittedDestination.country === country &&
    submittedDestination.postcode === postcode.trim() &&
    submittedDestination.region === effectiveRegion
    ? estimate.data
    : undefined;

  return (
    <section
      aria-labelledby="pdp-shipping-estimate-heading"
      className="mt-5 rounded-[var(--radius-lg)] border border-border p-4"
      data-testid="pdp-shipping-estimate"
    >
      <h2 id="pdp-shipping-estimate-heading" className="text-sm font-semibold text-fg">
        Estimate delivery
      </h2>
      <p className="mt-1 text-xs text-fg-muted">
        Enter your delivery destination for an estimate. Final availability and cost are confirmed at checkout.
      </p>
      {variantId && preview.data?.rates.length ? (
        <div className="mt-2 text-sm text-fg" data-testid="pdp-delivery-preview">
          <p>For {quantity} {quantity === 1 ? "item" : "items"} to eligible {countryName(previewCountry)} addresses:</p>
          <ul>{preview.data.rates.filter((rate) => rate.type !== "LOCAL_PICKUP" && !rate.requiresLiveResolve).map((rate) => <li key={rate.methodId}>{rate.name}: {rate.free ? "Free" : formatMoney(rate.amountMinor, rate.currency)}</li>)}</ul>
          <p className="mt-1 text-xs text-fg-muted">This product and quantity only. Basket contents, destination exclusions and final checkout can change availability and cost. Check your postcode below.</p>
          {preview.data.degraded && <p className="text-xs text-fg-muted">Shown in {preview.data.baseCurrency}; currency conversion is unavailable.</p>}
        </div>
      ) : <p className="mt-2 text-xs text-fg-muted">Enter a destination below to check applicable delivery prices.</p>}
      <form onSubmit={submit} className="mt-3 grid gap-2 sm:grid-cols-2">
        <label className="sr-only" htmlFor="pdp-estimate-country">Delivery country</label>
        <select id="pdp-estimate-country" autoComplete="country" value={countryInput} onChange={(event) => setCountryInput(event.target.value)} className="h-10 min-w-0 rounded-md border border-border bg-surface px-3 text-sm text-fg">
          <option value="">Choose delivery country</option>
          {[...MARKET_COUNTRY_CODES].sort(compareCountryCodes).map((code) => <option key={code} value={code}>{countryName(code)}</option>)}
        </select>
        <label className="sr-only" htmlFor="pdp-estimate-postcode">Postcode</label>
        <input
          id="pdp-estimate-postcode"
          autoComplete="postal-code"
          value={postcode}
          onChange={(event) => setPostcode(event.target.value)}
          placeholder="Postcode"
          className="h-10 rounded-md border border-border bg-surface px-3 text-sm text-fg"
        />
        {country && country !== "GB" && <>
          <label className="sr-only" htmlFor="pdp-estimate-region">State or province code (optional)</label>
          <input id="pdp-estimate-region" value={region} onChange={(event) => setRegion(event.target.value.toUpperCase())} autoComplete="address-level1" placeholder="State / province code, if needed" className="h-10 min-w-0 rounded-md border border-border bg-surface px-3 text-sm text-fg" />
        </>}
        <button
          type="submit"
          disabled={estimate.isFetching}
          className="h-10 rounded-[var(--btn-radius)] bg-secondary px-4 text-sm font-semibold text-secondary-fg disabled:opacity-50"
        >
          {estimate.isFetching ? "Checking…" : "Check"}
        </button>
      </form>
      <div aria-live="polite" className="mt-2">
        {validationError ? <p role="alert" className="text-xs text-danger">{validationError}</p> : null}
        {estimate.error ? (
          <p role="alert" className="text-xs text-danger">{estimate.error.message || "Could not estimate delivery. Check the destination and try again."}</p>
        ) : null}
        {result ? (
          <div className="mt-3 border-t border-border pt-3">
            <p className="text-xs text-fg-muted">
              For {quantity} {quantity === 1 ? "item" : "items"} to {submittedDestination!.country}, {submittedDestination!.postcode}
              {result.zone ? ` · ${result.zone.name}` : ""}
            </p>
            {result.rates.length > 0 ? (
              <ul className="mt-2 grid gap-2">
                {result.rates.map((rate) => (
                  <li key={rate.methodId} className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-fg">
                      {rate.name}
                      {rate.minDeliveryDays !== null && rate.maxDeliveryDays !== null
                        ? <span className="ml-1 text-xs text-fg-muted">({rate.minDeliveryDays}–{rate.maxDeliveryDays} days)</span>
                        : null}
                    </span>
                    <span className="shrink-0 font-medium text-fg">
                      {rate.requiresLiveResolve
                        ? "Final rate at checkout"
                        : rate.free
                          ? "Free"
                          : formatMoney(rate.amountMinor, rate.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-fg-muted">
                No delivery option could be quoted for this destination. Checkout will confirm availability.
              </p>
            )}
            {result.degraded ? (
              <p className="mt-2 text-xs text-fg-muted">
                Prices are shown in {result.baseCurrency} because {result.currency} conversion is unavailable.
              </p>
            ) : null}
            <p className="mt-2 text-xs text-fg-muted">
              Indicative only; final availability and any live carrier rate are confirmed at checkout.
            </p>
          </div>
        ) : null}
      </div>
    </section>
  );
}
